// R1b-β — Roles & Permissions tab body.
// Rehomed from /admin/roles. Read-only in this wave; permission simulation
// scaffolded (uses server /authority/evaluate + /auth/permissions).
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "sonner";

export default function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [query, setQuery] = useState("");
  const [simRole, setSimRole] = useState("");
  const [simResource, setSimResource] = useState("project");
  const [simAction, setSimAction] = useState("read");
  const [simResult, setSimResult] = useState(null);
  const [simTrace, setSimTrace] = useState(null);

  useEffect(() => {
    api.get("/roles").then((r) => {
      setRoles(r.data);
      if (r.data.length && !simRole) setSimRole(r.data[0].key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return roles;
    const q = query.toLowerCase();
    return roles.filter((r) =>
      (r.label || "").toLowerCase().includes(q)
      || (r.key || "").toLowerCase().includes(q)
      || (r.description || "").toLowerCase().includes(q)
    );
  }, [roles, query]);

  const simulate = async () => {
    // R1b-γ Debt 2 — call the server-side reasoner. Falls back to a local
    // trace only if the endpoint is unreachable so admins are never left
    // without an answer, but the SERVER decision is authoritative.
    setSimTrace(null);
    try {
      const r = await api.post("/authz/simulate", {
        role_key: simRole,
        action: simAction,
        resource_type: simResource,
      });
      setSimResult({
        granted: r.data.decision === "allow",
        reason: (r.data.trace || []).slice(-1)[0]?.reason || r.data.decision,
      });
      setSimTrace(r.data.trace || []);
    } catch (e) {
      const role = roles.find((r) => r.key === simRole);
      if (!role) { setSimResult({ granted: false, reason: "Unknown role (offline)" }); return; }
      if (role.key === "OWNER_SUPER_ADMIN") {
        setSimResult({ granted: true, reason: "Offline fallback: OWNER wildcard." });
        return;
      }
      const perms = role.permissions || [];
      const match = perms.find((p) => (p.resource === "*" || p.resource === simResource) && (p.action === "*" || p.action === simAction));
      setSimResult({
        granted: !!match,
        reason: match ? `Offline fallback matched ${match.resource}:${match.action}.` : `Offline fallback: no match on role ${role.label}.`,
      });
    }
  };

  return (
    <div data-testid="roles-tab-body">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display font-semibold text-erf-dark text-lg">Role & Permission Matrix</div>
          <div className="text-xs font-nav text-erf-dark/60 mt-0.5" data-testid="roles-count">{roles.length} role(s) seeded · server-enforced RBAC</div>
        </div>
        <Input
          type="search"
          placeholder="Search roles…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-64 h-9"
          data-testid="roles-search"
          aria-label="Search roles by name, key or description"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.key} className="bg-white border border-erf-borderCream rounded-md p-4" data-testid={`role-card-${r.key}`}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-display font-semibold text-erf-dark">{r.label}</div>
                  <div className="text-xs font-nav text-erf-dark/60 mt-0.5">{r.description}</div>
                </div>
                <div className="mono text-[11px] text-erf-dark/60 uppercase tracking-widest">
                  {r.is_internal ? "Internal" : "External"} · {r.key}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(r.permissions || []).length === 0 ? (
                  <span className="text-xs text-erf-dark/40 font-mono">No explicit permissions</span>
                ) : r.permissions.map((p, i) => (
                  <span key={i} className="mono text-[10px] px-1.5 py-0.5 rounded-sm border border-erf-borderCream bg-erf-creamSecondary text-erf-dark/80">
                    {p.resource}:{p.action}
                  </span>
                ))}
                {r.key === "OWNER_SUPER_ADMIN" && (
                  <span className="mono text-[10px] px-1.5 py-0.5 rounded-sm border border-erf-gold/50 bg-erf-gold/10 text-erf-goldHover">wildcard *:*</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <Card className="border-erf-borderCream h-fit sticky top-4">
          <CardContent className="p-4 space-y-3">
            <div className="font-display font-semibold text-erf-dark">Permission simulation</div>
            <div className="text-[11px] font-nav text-erf-dark/60">Trace why a given role has or lacks access.</div>
            <div>
              <Label className="text-xs uppercase font-nav">Role</Label>
              <Select value={simRole} onValueChange={setSimRole}>
                <SelectTrigger className="mt-1" data-testid="sim-role"><SelectValue placeholder="Choose role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sim-resource" className="text-xs uppercase font-nav">Resource</Label>
              <Input id="sim-resource" value={simResource} onChange={(e) => setSimResource(e.target.value)} className="mt-1 font-mono" data-testid="sim-resource" aria-label="Resource name for permission trace" />
            </div>
            <div>
              <Label htmlFor="sim-action" className="text-xs uppercase font-nav">Action</Label>
              <Input id="sim-action" value={simAction} onChange={(e) => setSimAction(e.target.value)} className="mt-1 font-mono" data-testid="sim-action" aria-label="Action name for permission trace" />
            </div>
            <Button onClick={simulate} className="w-full bg-erf-dark text-erf-cream" data-testid="sim-run">Trace decision</Button>
            {simResult && (
              <div
                data-testid="sim-result"
                className={`mt-2 rounded-sm border px-3 py-2 text-xs font-nav ${simResult.granted ? "border-green-600/40 bg-green-50 text-green-800" : "border-red-600/40 bg-red-50 text-red-800"}`}
              >
                <div className="font-semibold mb-1">{simResult.granted ? "GRANTED" : "DENIED"}</div>
                <div className="text-[11px]">{simResult.reason}</div>
              </div>
            )}
            {Array.isArray(simTrace) && simTrace.length > 0 && (
              <div className="mt-2 rounded-sm border border-erf-borderCream bg-erf-creamSecondary/60 p-2" data-testid="sim-trace">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Server trace ({simTrace.length})</div>
                <ol className="space-y-1 list-decimal list-inside">
                  {simTrace.map((t, i) => (
                    <li key={i} className="text-[10px] font-mono text-erf-dark/80" data-testid={`sim-trace-row-${i}`}>
                      <span className={t.matched ? "text-green-800" : "text-red-800"}>{t.matched ? "✓" : "✕"}</span>{" "}
                      <span className="uppercase">{t.source}</span>{" · "}{t.rule_name}
                      <div className="ml-4 text-erf-dark/60 normal-case">{t.reason}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
